import { Dispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";
import { baseUrl } from "../../config";

export interface Address {
  street: string;
  postalCode: string;
  city: string;
}

export enum InvoiceDeliveryMethod {
  EMAIL = "EMAIL",
  POST = "POST",
  E_INVOICE = "E_INVOICE", // optional future
}

//export type InvoiceDeliveryMethod = "EMAIL" | ""

export interface User {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  invoiceEmail?: string | null;
  phone: string;
  password: string;
  properties?: any;
  address?: Address;
  invoiceDeliveryMethod: InvoiceDeliveryMethod;
  isSuperAdmin?: boolean;
  isProtected?: boolean;
  protectionReason?: string;
  isVerified?: boolean;
}

export interface UserState {
  user: User | undefined;
  users: User[];
  open: boolean;
  editable: boolean;
  status: "idle" | "loading" | "failed";
}

const initialState: UserState = {
  user: undefined,
  users: [],
  open: false,
  editable: false,
  status: "idle",
};

export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async ({ associationId, role }: { associationId: number; role?: string }) => {
    const roleParam = role ? `&role=${role}` : "";
    const response = await axiosInstance(
      `/users?associationId=${associationId}${roleParam}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchUserById = createAsyncThunk(
  "user/fetchById",
  async (
    { id, associationId }: { id: string; associationId: number },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axiosInstance.get(
        `${baseUrl}/users/${id}?associationId=${associationId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  "user/changePassword",
  async (
    {
      id,
      currentPassword,
      newPassword,
      associationId,
    }: {
      id: string | undefined;
      currentPassword: string;
      newPassword: string;
      associationId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axiosInstance.put(
        `${baseUrl}/users/${id}/change-password`,
        { currentPassword, newPassword, associationId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Toggle user protection status (superadmin only)
 */
export const toggleUserProtection = createAsyncThunk(
  "user/toggleProtection",
  async (
    {
      userId,
      isProtected,
      protectionReason,
    }: { userId: number; isProtected: boolean; protectionReason?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/users/${userId}/protection`,
        {
          isProtected,
          protectionReason,
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Toggle superadmin status (superadmin only)
 */
export const toggleSuperAdmin = createAsyncThunk(
  "user/toggleSuperAdmin",
  async (
    { userId, isSuperAdmin }: { userId: number; isSuperAdmin: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/users/${userId}/superadmin`,
        {
          isSuperAdmin,
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Get user protection status
 */
export const fetchUserProtection = createAsyncThunk(
  "user/fetchProtection",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/users/${userId}/protection`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    addUser(state, action) {
      state.users.push(action.payload);
    },
    updateUser(state, action) {
      const userId = state.users.findIndex((u) => u.id === action.payload.id);
      state.users[userId] = action.payload;
    },
    deleteUser(state, action) {
      state.users = state.users.filter((u: any) => u.id !== action.payload);
    },
    setOpen(state, action) {
      state.open = action.payload;
    },
    setEditable(state) {
      state.editable = !state.editable;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUsers.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      state.users = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchUsers.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchUserById.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchUserById.fulfilled, (state, action) => {
      state.user = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchUserById.rejected, (state) => {
      state.status = "failed";
    });

    // Toggle protection
    builder.addCase(toggleUserProtection.fulfilled, (state, action) => {
      // Update user in users array if present
      const userIndex = state.users.findIndex(
        (u) => u.id === action.payload.id
      );
      if (userIndex !== -1) {
        state.users[userIndex].isProtected = action.payload.isProtected;
        state.users[userIndex].protectionReason =
          action.payload.protectionReason;
      }
      // Update current user if it's the same
      if (state.user && state.user.id === action.payload.id) {
        state.user.isProtected = action.payload.isProtected;
        state.user.protectionReason = action.payload.protectionReason;
      }
    });

    // Toggle superadmin
    builder.addCase(toggleSuperAdmin.fulfilled, (state, action) => {
      // Update user in users array if present
      const userIndex = state.users.findIndex(
        (u) => u.id === action.payload.id
      );
      if (userIndex !== -1) {
        state.users[userIndex].isSuperAdmin = action.payload.isSuperAdmin;
      }
      // Update current user if it's the same
      if (state.user && state.user.id === action.payload.id) {
        state.user.isSuperAdmin = action.payload.isSuperAdmin;
      }
    });

    // Activate user
    builder.addCase(activateUser.fulfilled, (state, action) => {
      const idx = state.users.findIndex((u) => u.id === action.payload.userId);
      if (idx !== -1) state.users[idx].isVerified = true;
      if (state.user && state.user.id === action.payload.userId)
        state.user.isVerified = true;
    });

    // Fetch protection status
    builder.addCase(fetchUserProtection.fulfilled, (state, action) => {
      // Update user in users array if present
      const userIndex = state.users.findIndex(
        (u) => u.id === action.payload.id
      );
      if (userIndex !== -1) {
        state.users[userIndex].isProtected = action.payload.isProtected;
        state.users[userIndex].protectionReason =
          action.payload.protectionReason;
        state.users[userIndex].isSuperAdmin = action.payload.isSuperAdmin;
      }
      // Update current user if it's the same
      if (state.user && state.user.id === action.payload.id) {
        state.user.isProtected = action.payload.isProtected;
        state.user.protectionReason = action.payload.protectionReason;
        state.user.isSuperAdmin = action.payload.isSuperAdmin;
      }
    });
  },
});

export function setUser(user: any) {
  return async (dispatch: Dispatch) => {
    dispatch(userSlice.actions.setUser(user));
  };
}

export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (
    {
      id,
      data,
      associationId,
    }: { id: any; data: Partial<User>; associationId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(
        `/users/${id}?associationId=${associationId}`,
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export function createUser(user: any, associationId: any) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axiosInstance.post(`/users`, {
        ...user,
        associationId,
      });
      dispatch(userSlice.actions.addUser(response.data));
    } catch (error: any) {
      throw error;
    }
  };
}

export function deleteUser(id: number, associationId: number) {
  return async (dispatch: Dispatch) => {
    try {
      await axiosInstance.delete(`/users/${id}?associationId=${associationId}`);
      dispatch(userSlice.actions.deleteUser(id));
    } catch (error: any) {
      throw error;
    }
  };
}

export function editUserForm() {
  return async (dispatch: Dispatch) => {
    dispatch(userSlice.actions.setEditable());
  };
}

export function openUserForm(open: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(userSlice.actions.setOpen(open));
  };
}

export const activateUser = createAsyncThunk(
  "user/activateUser",
  async (
    { userId, associationId }: { userId: number; associationId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(`/users/${userId}/activate`, {
        associationId,
      });
      return { userId, ...response.data };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Virhe aktivoinnissa"
      );
    }
  }
);

export const sendAllVerifications = createAsyncThunk(
  "user/sendAllVerifications",
  async (associationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/users/send-all-verifications",
        {
          associationId,
        }
      );
      return response.data as { sent: number; skipped: number };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Virhe linkkien lähetyksessä"
      );
    }
  }
);

export const sendUserVerification = createAsyncThunk(
  "user/sendVerification",
  async (
    { userId, associationId }: { userId: number; associationId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/users/${userId}/send-verification`,
        { associationId }
      );
      return { userId, ...response.data };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Virhe aktivointilinkin lähetyksessä"
      );
    }
  }
);

export default userSlice.reducer;
